import { Fragment, memo, useCallback } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import PromotionCodeContainer from "app/pages/Booking/PromotionCode/PromotionCodeContainer";
import PaymentCouponsTitle from "app/pages/Booking/Payment/PaymentCoupons/PaymentCouponsTitle/PaymentCouponsTitle";
import CouponsCguPopoverButton from "app/pages/Booking/Payment/PaymentCoupons/CouponsCGUPopoverButton/CouponsCGUPopoverButton";
import PromotionCodeRadio from "app/pages/Booking/PromotionCode/PromotionCodeRadio";
import CreditRadio from "app/pages/Booking/Payment/PaymentCoupons/CreditRadio/CreditRadio";
import "./PaymentCoupons.scss";
import LoaderBar from "app/pages/.shared/LoaderBar/LoaderBar";
import {
	ALERT_TYPE,
	PAYMENT_COUPON_TYPE,
	PROMO_CODE_STATUS,
	USABLE_CREDITS_STATUS,
} from "app/constants";
import {
	sendTagOnCGUCreditClick,
	sendTagOnOnlyNonApplicableCouponMessageShow,
} from "app/utils/analytics";
import OnlyNonApplicableCouponMessage from "app/pages/Booking/Payment/PaymentCoupons/OnlyNonApplicableCouponMessage/OnlyNonApplicableCouponMessage";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import PaymentCreditsTitleTypography from "app/pages/Booking/Payment/PaymentCoupons/PaymentCreditsTitleTypography/PaymentCreditsTitleTypography";
import AlertMessage from "app/pages/.shared/AlertMessage";
import AmountContainer from "app/pages/.shared/AmountContainer";
import { TagFilledIcon } from "app/icons/finance";

const PaymentCoupons = ({
	enablePromotionCode = false,
	promotionStatus,
	promotionMessage,
	promotionValue,
	enableCredits = false,
	totalUsableCredit = 0,
	isStopover,
	totalUsableCreditNote = 0,
	usableCredits = [],
	unusableCredits = [],
	onlyNonApplicableCredits = [],
	onChange = () => {},
	onValidPromoCodeApplied = () => {},
	usableCouponsStatus,
	onPromoCodeFocus = () => {},
	couponType,
}) => {
	const hasCredits = totalUsableCredit > 0;
	const hasCreditNotes = totalUsableCreditNote > 0;
	const hasOnlyNonApplicableCredits =
		!hasCredits &&
		unusableCredits.length === onlyNonApplicableCredits.length &&
		onlyNonApplicableCredits.length > 0;

	const promocodeComponent = hasCredits ? (
		<PromotionCodeRadio
			onChange={onChange}
			onValidPromoCodeApplied={onValidPromoCodeApplied}
			onPromoCodeFocus={onPromoCodeFocus}
			couponType={couponType}
			isStopover={isStopover}
		/>
	) : (
		<PromotionCodeContainer
			onValidPromoCodeApplied={onValidPromoCodeApplied}
			isStopover={isStopover}
		/>
	);

	const isLoadingAllCoupons = usableCouponsStatus === USABLE_CREDITS_STATUS.LOADING_COUPONS;

	const handleOnCreditCGUClick = useCallback(() => {
		sendTagOnCGUCreditClick();
	}, []);

	const handleOnOnlyNonApplicableCreditMessageShow = useCallback(() => {
		sendTagOnOnlyNonApplicableCouponMessageShow("credit", onlyNonApplicableCredits);
	}, [onlyNonApplicableCredits]);

	const shouldShowCreditPromoCodeSection =
		hasOnlyNonApplicableCredits || enablePromotionCode || hasCredits || isLoadingAllCoupons;

	const creditComponent = hasCredits ? (
		<CreditRadio
			value={totalUsableCredit}
			usableCredits={usableCredits}
			unusableCredits={unusableCredits}
			onChange={onChange}
			couponType={couponType}
		/>
	) : null;

	const isPromoCodeActive = couponType === PAYMENT_COUPON_TYPE.PROMOCODE;

	const isValidPromocodeApplied =
		isPromoCodeActive && (promotionStatus === PROMO_CODE_STATUS.SUCCESS || promotionValue > 0);

	const isUnvalidPromocodeApplied =
		isPromoCodeActive && promotionStatus === PROMO_CODE_STATUS.INVALID_PROMOCODE;

	let promoCodeMessage;

	if (isValidPromocodeApplied) {
		promoCodeMessage = (
			<div className="payment-credits__success" data-testid="promotion-code-success">
				<TagFilledIcon className="payment-credits__success-icon" />
				<Typography variant={TYPOGRAPHY_VARIANTS.REGULAR}>
					{promotionMessage || (
						<FormattedMessage
							id="promotion.code.success.message"
							values={{ amount: <AmountContainer amount={promotionValue} /> }}
						/>
					)}
				</Typography>
			</div>
		);
	} else if (isUnvalidPromocodeApplied) {
		promoCodeMessage = promotionMessage ? (
			<AlertMessage message={promotionMessage} alertType={ALERT_TYPE.ERROR} />
		) : (
			<AlertMessage
				message={<FormattedMessage id="promotion.code.error.message" />}
				alertType={ALERT_TYPE.ERROR}
			/>
		);
	}

	return (
		(shouldShowCreditPromoCodeSection || hasCreditNotes) && (
			<div className="payment-credits" data-testid="payment-credits">
				{shouldShowCreditPromoCodeSection && (
					<Fragment>
						<Typography
							variant={TYPOGRAPHY_VARIANTS.XL3}
							variantMD={TYPOGRAPHY_VARIANTS.XL4}
							isBold
							className="payment__title"
							component="h2"
						>
							<FormattedMessage id="booking.aside.coupons.title" />
						</Typography>
						<div className="payment__content">
							{hasOnlyNonApplicableCredits && (
								<OnlyNonApplicableCouponMessage
									title={
										<FormattedMessage id="payment.coupons.only.not.applicable.warning.title" />
									}
									message={
										<FormattedMessage id="payment.coupons.only.not.applicable.warning.message" />
									}
									coupons={onlyNonApplicableCredits}
									onShow={handleOnOnlyNonApplicableCreditMessageShow}
								/>
							)}

							{(enablePromotionCode || hasCredits) && (
								<PaymentCreditsTitleTypography
									text={
										isLoadingAllCoupons ? (
											<LoaderBar width={340} />
										) : (
											<PaymentCouponsTitle
												isPromocodeEnabled={enablePromotionCode}
												hasUsableCredits={hasCredits}
											/>
										)
									}
									suffix={
										!isLoadingAllCoupons && (
											<CouponsCguPopoverButton
												isPromotionCodeEnabled={enablePromotionCode}
												hasCredits={hasCredits}
												onClick={handleOnCreditCGUClick}
											/>
										)
									}
								/>
							)}

							{enableCredits && (
								<div className="payment-credits__item">
									{isLoadingAllCoupons ? (
										<LoaderBar height={30} />
									) : (
										creditComponent
									)}
								</div>
							)}

							{enablePromotionCode && (
								<div className="payment-credits__item">
									{isLoadingAllCoupons ? (
										<LoaderBar height={30} />
									) : (
										promocodeComponent
									)}
									<div className="payment-credits__promotion-message">
										{promoCodeMessage}
									</div>
								</div>
							)}
						</div>
					</Fragment>
				)}
			</div>
		)
	);
};

PaymentCoupons.propTypes = {
	enablePromotionCode: PropTypes.bool,
	isStopover: PropTypes.bool,
	enableCredits: PropTypes.bool,
	promotionMessage: PropTypes.string,
	promotionValue: PropTypes.number,
	totalUsableCredit: PropTypes.number,
	totalUsableCreditNote: PropTypes.number,
	usableCredits: PropTypes.array,
	unusableCredits: PropTypes.array,
	onlyNonApplicableCredits: PropTypes.array,
	usableCouponsStatus: PropTypes.string,
	promotionStatus: PropTypes.string,
	onChange: PropTypes.func,
	onValidPromoCodeApplied: PropTypes.func,
	onPromoCodeFocus: PropTypes.func,
	couponType: PropTypes.string,
};

export default memo(PaymentCoupons);
